import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext.jsx";
import { useCart } from "@/cart/CartContext.jsx";
import { useFavorites } from "@/favorites/FavoritesContext.jsx";
import { extractUsername } from "@/auth/token.js";
import { getAdminUser } from "@/api/admin.api.js";
import { getUser } from "@/api/customer.api.js";
import {
    ADMIN_LOGIN_ROUTE,
    ADMIN_PROFILE_ROUTE,
    CART_ROUTE,
    CUSTOMER_PROFILE_ROUTE,
    MAIN_ROUTE,
    ROLES
} from "@/utils/constants.js";
import { CUSTOMER_PROFILE_MENU, ACCOUNT_MENU_CART, ACCOUNT_MENU_LOGOUT, staffProfileMenuForRole } from "@/utils/profileMenus.js";
import { isStaffRole } from "@/utils/staffAccess.js";
import MenuItemLabel from "@/components/MenuItemLabel.jsx";
import { customerDisplayName } from "@/utils/customerProfile.js";
import { productImageUrl } from "@/utils/format.js";
import { useViewportAnchoredMenu, canHoverFinePointer } from "@/hooks/useViewportAnchoredMenu.js";

function getInitials(displayName) {
    const parts = displayName.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return (parts[0]?.[0] || "?").toUpperCase();
}

export default function UserProfileMenu() {
    const { token, role, logout } = useAuth();
    const { itemCount } = useCart();
    const { itemCount: favoriteCount } = useFavorites();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [profile, setProfile] = useState(null);
    const [open, setOpen] = useState(false);
    const closeTimerRef = useRef(null);
    const menuRef = useRef(null);
    const triggerRef = useRef(null);
    const dropdownRef = useRef(null);

    useViewportAnchoredMenu(open, triggerRef, dropdownRef, { preferAlign: "end" });

    const isCustomer = role === ROLES.CUSTOMER;
    const isStaff = isStaffRole(role);
    const profileRoute = isStaff ? ADMIN_PROFILE_ROUTE : CUSTOMER_PROFILE_ROUTE;
    const menuItems = isStaff ? staffProfileMenuForRole(role) : CUSTOMER_PROFILE_MENU;
    const activeSection = searchParams.get("section");
    const onProfilePage = location.pathname === profileRoute
        || location.pathname.startsWith(`${profileRoute}/`);
    const onCartPage = location.pathname === CART_ROUTE
        || location.pathname.startsWith(`${CART_ROUTE}/`);
    const cartLabel = itemCount > 0 ? `Mon panier (${itemCount})` : "Mon panier";
    const menuLabel = (item) => {
        if (item.id === "favorites" && isCustomer && favoriteCount > 0) {
            return `Mes Favoris (${favoriteCount})`;
        }
        return item.label;
    };
    const displayName = profile
        ? customerDisplayName(profile) || profile.username || extractUsername(token) || "Mon compte"
        : extractUsername(token) || "Mon compte";
    const photoUrl = productImageUrl(profile?.photoId);

    useEffect(() => {
        let cancelled = false;

        async function loadProfile() {
            if (role === ROLES.CUSTOMER) {
                try {
                    const user = await getUser();
                    if (!cancelled) {
                        setProfile(user);
                    }
                    return;
                } catch {
                    // fall through to username
                }
            }

            if (isStaffRole(role)) {
                try {
                    const user = await getAdminUser();
                    if (!cancelled) {
                        setProfile(user);
                    }
                    return;
                } catch {
                    // fall through to username
                }
            }

            if (!cancelled) {
                setProfile(null);
            }
        }

        loadProfile();
        return () => {
            cancelled = true;
        };
    }, [token, role]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const cancelClose = () => {
        if (closeTimerRef.current) {
            window.clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    };

    const scheduleClose = () => {
        cancelClose();
        closeTimerRef.current = window.setTimeout(() => setOpen(false), 180);
    };

    const openMenu = () => {
        cancelClose();
        setOpen(true);
    };

    const handleMouseEnter = () => {
        if (canHoverFinePointer()) {
            openMenu();
        }
    };

    const handleMouseLeave = () => {
        if (canHoverFinePointer()) {
            scheduleClose();
        }
    };

    const toggleMenu = () => {
        setOpen((current) => !current);
    };

    const goToSection = (sectionId) => {
        setOpen(false);
        navigate(`${profileRoute}?section=${sectionId}`);
    };

    const goToCart = () => {
        setOpen(false);
        navigate(CART_ROUTE);
    };

    const handleLogout = () => {
        setOpen(false);
        const wasStaff = isStaffRole(role);
        logout();
        navigate(wasStaff ? ADMIN_LOGIN_ROUTE : MAIN_ROUTE);
    };

    return (
        <div
            ref={menuRef}
            className={`user-profile-menu${open ? " is-open" : ""}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <button
                ref={triggerRef}
                type="button"
                className="user-profile-trigger"
                onClick={toggleMenu}
                aria-expanded={open}
                aria-haspopup="true"
            >
                {photoUrl ? (
                    <img src={photoUrl} alt="" className="user-profile-avatar user-profile-avatar--photo" />
                ) : (
                    <span className="user-profile-avatar" aria-hidden="true">{getInitials(displayName)}</span>
                )}
                <span className="user-profile-name">{displayName}</span>
                <span className="user-profile-chevron" aria-hidden="true" />
            </button>

            <div
                ref={dropdownRef}
                className="user-profile-dropdown"
                onMouseEnter={canHoverFinePointer() ? cancelClose : undefined}
            >
                {isCustomer && (
                    <button
                        type="button"
                        className={`user-profile-dropdown-item${onCartPage ? " is-active" : ""}`}
                        onClick={goToCart}
                        title={cartLabel}
                        aria-current={onCartPage ? "page" : undefined}
                        data-tour="account-menu-cart"
                    >
                        <MenuItemLabel icon={ACCOUNT_MENU_CART.icon}>{cartLabel}</MenuItemLabel>
                    </button>
                )}
                {menuItems.map((item) => (
                    <button
                        key={item.id}
                        type="button"
                        className={`user-profile-dropdown-item${
                            onProfilePage && activeSection === item.id ? " is-active" : ""
                        }`}
                        onClick={() => goToSection(item.id)}
                        title={menuLabel(item)}
                        aria-current={onProfilePage && activeSection === item.id ? "page" : undefined}
                    >
                        <MenuItemLabel icon={item.icon}>{menuLabel(item)}</MenuItemLabel>
                    </button>
                ))}
                <div className="user-profile-dropdown-divider" />
                <button
                    type="button"
                    className="user-profile-dropdown-item user-profile-dropdown-item--logout"
                    onClick={handleLogout}
                >
                    <MenuItemLabel icon={ACCOUNT_MENU_LOGOUT.icon} danger>
                        {ACCOUNT_MENU_LOGOUT.label}
                    </MenuItemLabel>
                </button>
            </div>
        </div>
    );
}
